'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import {
  Check,
  Circle,
  Loader2,
  X,
  SkipForward,
  Sparkles,
} from 'lucide-react'
import type {
  InteractiveElement,
  ActionButtonData,
  SuggestionData,
  OptionGroupData,
  TaskListData,
  TaskItem,
} from '@/types/botArtifacts'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InteractiveElementsBlockProps {
  elements: InteractiveElement[]
  onRespondToElement?: (elementId: string, actionMetadata: Record<string, unknown>) => Promise<void>
  onSendMessage?: (content: string) => Promise<void>
}

// ---------------------------------------------------------------------------
// Main block
// ---------------------------------------------------------------------------

export function InteractiveElementsBlock({
  elements,
  onRespondToElement,
  onSendMessage,
}: InteractiveElementsBlockProps) {
  return (
    <div className="my-2 flex flex-col gap-2">
      {elements.map(element => (
        <InteractiveElementRenderer
          key={element.elementId}
          element={element}
          onRespondToElement={onRespondToElement}
          onSendMessage={onSendMessage}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Element dispatcher
// ---------------------------------------------------------------------------

function InteractiveElementRenderer({
  element,
  onRespondToElement,
  onSendMessage,
}: {
  element: InteractiveElement
  onRespondToElement?: (elementId: string, actionMetadata: Record<string, unknown>) => Promise<void>
  onSendMessage?: (content: string) => Promise<void>
}) {
  switch (element.type) {
    case 'action-button':
      return (
        <ActionButtonElement
          element={element}
          data={element.data as ActionButtonData}
          onRespondToElement={onRespondToElement}
        />
      )
    case 'suggestion':
      return (
        <SuggestionElement
          element={element}
          data={element.data as SuggestionData}
          onSendMessage={onSendMessage}
        />
      )
    case 'option-group':
      return (
        <OptionGroupElement
          element={element}
          data={element.data as OptionGroupData}
          onRespondToElement={onRespondToElement}
        />
      )
    case 'task-list':
      return (
        <TaskListElement
          element={element}
          data={element.data as TaskListData}
        />
      )
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Action Button
// ---------------------------------------------------------------------------

function ActionButtonElement({
  element,
  data,
  onRespondToElement,
}: {
  element: InteractiveElement
  data: ActionButtonData
  onRespondToElement?: (elementId: string, actionMetadata: Record<string, unknown>) => Promise<void>
}) {
  const [isExecuting, setIsExecuting] = useState(false)

  const variantMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost'> = {
    primary: 'default',
    default: 'default',
    secondary: 'secondary',
    destructive: 'destructive',
    outline: 'outline',
    ghost: 'ghost',
  }

  const variant = variantMap[element.options?.variant as string ?? 'default'] || 'default'

  const handleClick = async () => {
    if (!onRespondToElement || isExecuting) return
    setIsExecuting(true)
    try {
      await onRespondToElement(element.elementId, data.actionMetadata)
    } catch {
      // Error handled by caller
    } finally {
      setIsExecuting(false)
    }
  }

  return (
    <Button
      size="sm"
      variant={variant}
      disabled={isExecuting}
      onClick={handleClick}
      className="my-0.5"
    >
      {isExecuting ? (
        <>
          <Loader2 className="mr-1.5 size-3.5 animate-spin" />
          Running...
        </>
      ) : (
        <>
          {element.icon && <span className="mr-1.5">{element.icon}</span>}
          {element.label}
        </>
      )}
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Suggestion (chip/pill)
// ---------------------------------------------------------------------------

function SuggestionElement({
  element,
  data,
  onSendMessage,
}: {
  element: InteractiveElement
  data: SuggestionData
  onSendMessage?: (content: string) => Promise<void>
}) {
  const [clicked, setClicked] = useState(false)

  if (clicked) return null

  const handleClick = async () => {
    if (!onSendMessage) return
    setClicked(true)
    try {
      await onSendMessage(data.prompt)
    } catch {
      setClicked(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5',
        'px-3 py-1.5 text-sm text-primary transition-colors',
        'hover:bg-primary/10 hover:border-primary/30',
      )}
    >
      <Sparkles className="size-3" />
      {element.label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Option Group
// ---------------------------------------------------------------------------

function OptionGroupElement({
  element,
  data,
  onRespondToElement,
}: {
  element: InteractiveElement
  data: OptionGroupData
  onRespondToElement?: (elementId: string, actionMetadata: Record<string, unknown>) => Promise<void>
}) {
  const isMultiple = element.options?.selectionMode === 'multiple'
  const layout = (element.options?.layout as string) || 'vertical'
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleItem = (value: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (isMultiple) {
        if (next.has(value)) next.delete(value)
        else next.add(value)
      } else {
        next.clear()
        next.add(value)
      }
      return next
    })
  }

  const handleSubmit = async () => {
    if (!onRespondToElement || selected.size === 0 || isSubmitting) return
    setIsSubmitting(true)
    try {
      await onRespondToElement(element.elementId, {
        ...data.callbackMetadata,
        selectedValues: Array.from(selected),
      })
    } catch {
      // Error handled by caller
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-3">
      {element.label && (
        <p className="mb-2 text-sm font-medium">{element.label}</p>
      )}
      {element.description && (
        <p className="mb-2 text-xs text-muted-foreground">{element.description}</p>
      )}
      <div className={cn(
        'gap-2',
        layout === 'horizontal' ? 'flex flex-wrap' : 'flex flex-col',
      )}>
        {data.items.map(item => {
          const isSelected = selected.has(item.value)
          return (
            <button
              key={item.value}
              type="button"
              onClick={() => toggleItem(item.value)}
              className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                isSelected
                  ? 'border-primary bg-primary/5 text-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted/50',
              )}
            >
              <div className={cn(
                'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm border',
                isMultiple ? 'rounded-sm' : 'rounded-full',
                isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/30',
              )}>
                {isSelected && <Check className="size-3" />}
              </div>
              <div>
                <span className="font-medium">{item.label}</span>
                {item.description && (
                  <span className="mt-0.5 block text-xs text-muted-foreground">{item.description}</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
      <div className="mt-3">
        <Button
          size="sm"
          disabled={selected.size === 0 || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Task List
// ---------------------------------------------------------------------------

function TaskListElement({
  element,
  data,
}: {
  element: InteractiveElement
  data: TaskListData
}) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const showProgress = element.options?.showProgress !== false
  const collapsible = element.options?.collapsible === true
  const progressPercent = data.totalCount > 0 ? (data.completedCount / data.totalCount) * 100 : 0

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{element.label || 'Tasks'}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {data.completedCount}/{data.totalCount}
          </span>
          {collapsible && (
            <button
              type="button"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {isCollapsed ? 'Show' : 'Hide'}
            </button>
          )}
        </div>
      </div>

      {showProgress && (
        <Progress value={progressPercent} className="mt-2 h-1.5" />
      )}

      {data.summary && (
        <p className="mt-1.5 text-xs text-muted-foreground">{data.summary}</p>
      )}

      {!isCollapsed && (
        <div className="mt-2 space-y-1">
          {data.tasks.map(task => (
            <TaskItemRow key={task.taskId} task={task} />
          ))}
        </div>
      )}
    </div>
  )
}

function TaskItemRow({ task }: { task: TaskItem }) {
  const iconMap: Record<string, React.ReactNode> = {
    pending: <Circle className="size-3.5 text-muted-foreground" />,
    in_progress: <Loader2 className="size-3.5 animate-spin text-blue-500" />,
    completed: <Check className="size-3.5 text-green-600" />,
    failed: <X className="size-3.5 text-red-500" />,
    skipped: <SkipForward className="size-3.5 text-muted-foreground" />,
  }

  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="mt-0.5 shrink-0">
        {iconMap[task.status] || <Circle className="size-3.5 text-muted-foreground" />}
      </span>
      <div className="min-w-0 text-sm">
        <span className={cn(
          task.status === 'completed' && 'text-muted-foreground line-through',
          task.status === 'failed' && 'text-red-600 dark:text-red-400',
          task.status === 'skipped' && 'text-muted-foreground',
        )}>
          {task.description}
        </span>
        {task.result && (
          <span className="ml-1.5 text-xs text-muted-foreground">— {task.result}</span>
        )}
      </div>
    </div>
  )
}
